import React from 'react';

function InvestmentsTechnology() {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[56vh] min-h-[420px] overflow-hidden">
        <img src="/images/technology/engineer-4904884_1280.jpg" alt="Technology" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/60 to-black/80" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.02em]">Technology</h1>
            <p className="mt-4 text-white/90 max-w-2xl text-lg">Software, AI, consumer internet, and enterprise automation transforming industries.</p>
          </div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Technology Investment Strategy</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">We back software and technology companies with defensible moats, strong unit economics, and massive market opportunities.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-start mt-10">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">The Technology Opportunity</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Digital transformation is accelerating across every industry. Cloud adoption, AI/ML capabilities, and API-first architectures are enabling new business models and unprecedented productivity gains. We invest in companies building the infrastructure, applications, and intelligence layers of the modern software stack.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our focus spans vertical SaaS targeting large, underserved industries; horizontal platforms with network effects; AI/ML companies solving high-value problems; and fintech infrastructure modernizing financial services. We prioritize companies with strong founder-market fit, product-led growth, and clear paths to $100M+ ARR.
              </p>
              <div className="bg-blue-50 border-l-4 border-[#0056d7] p-5 rounded-r-lg">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong className="text-[#0056d7]">Market Context:</strong> Global software spending exceeds $1 trillion annually, growing at 11% CAGR. SaaS companies at scale demonstrate 80%+ gross margins and industry-leading cash generation.
                </p>
              </div>
            </div>
            <div className="relative">
              <img src="/images/technology/ux-787980_1280.jpg" alt="Technology thesis" className="w-full h-96 object-cover rounded-2xl shadow-xl" />
              <div className="absolute -bottom-5 -right-5 w-40 h-40 bg-[#0056d7] opacity-10 rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-semibold text-gray-900 text-center mb-10">Investment Focus Areas</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {t:'Vertical SaaS', d:'Industry-specific software for healthcare, construction, logistics, legal, and other large, fragmented markets with deep workflows.', img:'/images/technology/ux-787980_1280.jpg'},
              {t:'AI & Data Platforms', d:'Machine learning infrastructure, data analytics, computer vision, NLP, and AI-powered automation for enterprise workflows.', img:'/images/technology/engineer-4904884_1280.jpg'},
              {t:'Fintech Infrastructure', d:'Payment rails, embedded finance, lending infrastructure, KYC/AML, treasury management, and B2B financial software.', img:'/images/technology/iphone-676767_1280.jpg'}
            ].map(x => (
              <article key={x.t} className="rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300">
                <img src={x.img} alt={x.t} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{x.t}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{x.d}</p>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-10 grid md:grid-cols-4 gap-5">
            {[
              {t:'Developer Tools', d:'APIs, SDKs, cloud infrastructure, CI/CD, observability, and developer experience platforms.'},
              {t:'Marketplace & Networks', d:'B2B marketplaces, supply chain networks, and two-sided platforms with strong network effects.'},
              {t:'Consumer Internet', d:'Social platforms, creator economy tools, gaming, and consumer subscription services.'},
              {t:'Enterprise Automation', d:'RPA, workflow automation, integration platforms, and no-code/low-code solutions.'}
            ].map(item => (
              <div key={item.t} className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">{item.t}</h4>
                <p className="text-xs text-gray-600 leading-relaxed">{item.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Criteria */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">What We Look For</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">Our investment framework prioritizes product-market fit, scalable go-to-market, and defensible competitive advantages.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Key Investment Criteria</h3>
              <div className="space-y-4">
                {[
                  {title: 'Product & Technology', points: ['Strong technical team and architecture', 'Clear product differentiation', 'High customer NPS (60+)', 'Product-led growth motion']},
                  {title: 'Market & Traction', points: ['$1B+ TAM with expansion potential', '$1M+ ARR with 3x YoY growth', '100%+ net revenue retention', 'Strong unit economics (CAC < 12mo payback)']},
                  {title: 'Team & Execution', points: ['Founder-market fit', 'Technical & commercial balance', 'Proven ability to scale', 'Culture of experimentation']}
                ].map((section, idx) => (
                  <div key={idx} className="border-l-4 border-[#0056d7] pl-5 bg-blue-50/50 py-3 rounded-r-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">{section.title}</h4>
                    <ul className="space-y-1">
                      {section.points.map((point, i) => (
                        <li key={i} className="text-sm text-gray-600 flex gap-2">
                          <span className="text-[#0056d7]">•</span> {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <img src="/images/technology/iphone-830480_1280.jpg" alt="Technology investment" className="w-full h-64 object-cover rounded-2xl shadow-lg" />
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6 rounded-xl">
                <h4 className="font-semibold mb-4">Value-Add Support</h4>
                <ul className="space-y-2 text-sm text-white/90">
                  <li className="flex gap-2"><span className="text-yellow-400">✓</span> Go-to-market strategy & sales playbook development</li>
                  <li className="flex gap-2"><span className="text-yellow-400">✓</span> Executive recruiting & team building</li>
                  <li className="flex gap-2"><span className="text-yellow-400">✓</span> Customer introductions & partnership facilitation</li>
                  <li className="flex gap-2"><span className="text-yellow-400">✓</span> Financial modeling & metrics optimization</li>
                  <li className="flex gap-2"><span className="text-yellow-400">✓</span> Follow-on funding & exit strategy</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Success */}
      <section className="py-16 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold">Portfolio Performance</h2>
            <p className="mt-3 text-white/80 max-w-3xl mx-auto">Our technology portfolio companies achieve best-in-class growth, retention, and capital efficiency.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {metric: '180%', label: 'Median ARR growth (YoY)'},
              {metric: '125%', label: 'Average net revenue retention'},
              {metric: '<9mo', label: 'Median CAC payback period'},
              {metric: '15+', label: 'Portfolio companies scaled to $10M+ ARR'}
            ].map((item, idx) => (
              <div key={idx} className="text-center bg-white/10 backdrop-blur rounded-xl p-6 border border-white/20">
                <div className="text-4xl font-bold text-yellow-400 mb-2">{item.metric}</div>
                <div className="text-sm text-white/80">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-semibold text-gray-900">Partner with Us</h3>
          <p className="mt-2 text-gray-600">Access our network, expertise, and capital to scale your technology company.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="/apply" className="px-6 py-3 rounded-md bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-semibold hover:shadow-lg transition-shadow">Apply now</a>
            <a href="/contact" className="px-6 py-3 rounded-md border-2 border-gray-300 text-gray-700 hover:border-gray-400 transition-colors">Contact us</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default InvestmentsTechnology;


